import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../../../components/modal/modal.component';
import { TrashIcon, Users } from '../../../assets';
import { apiService, ApiError, ShareUser } from '../../../services/api.service';
import styles from './share-meeting-modal.module.scss';

type Props = {
  show: boolean;
  meetingId: string;
  meetingName?: string;
  onClose: () => void;
};

function initials(user: ShareUser): string {
  const source = (user.full_name || user.email || '?').trim();
  return source.charAt(0).toUpperCase() || '?';
}

const ShareMeetingModal: React.FC<Props> = ({ show, meetingId, meetingName, onClose }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [shares, setShares] = useState<ShareUser[]>([]);

  const [candidateUsers, setCandidateUsers] = useState<ShareUser[] | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [addingSelected, setAddingSelected] = useState(false);

  const [emailInput, setEmailInput] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);

  const [revokingId, setRevokingId] = useState<number | null>(null);

  const reportError = useCallback((e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.status === 403) {
      setForbidden(true);
      return;
    }
    toast.error(e instanceof Error ? e.message : fallback);
  }, []);

  const loadShares = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const data = await apiService.getMeetingShares(meetingId);
      setShares(data);
    } catch (e) {
      reportError(e, t('file.share-modal.load-error'));
    } finally {
      setLoading(false);
    }
  }, [meetingId, reportError, t]);

  const loadCandidateUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await apiService.getUsersForSharing();
      setCandidateUsers(data);
    } catch {
      // Не-админ или недоступный сервис — тихо уходим в fallback по email.
      setCandidateUsers(null);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show || !meetingId) return;
    setSearch('');
    setSelectedIds(new Set());
    setEmailInput('');
    void loadShares();
    void loadCandidateUsers();
  }, [show, meetingId, loadShares, loadCandidateUsers]);

  const sharedIds = useMemo(() => new Set(shares.map(s => s.id)), [shares]);

  const filteredCandidates = useMemo(() => {
    if (!candidateUsers) return [];
    const query = search.trim().toLowerCase();
    return candidateUsers
      .filter(u => !sharedIds.has(u.id))
      .filter(u => !query
        || u.email.toLowerCase().includes(query)
        || (u.full_name || '').toLowerCase().includes(query));
  }, [candidateUsers, sharedIds, search]);

  const toggleSelected = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (!selectedIds.size || addingSelected) return;
    setAddingSelected(true);
    try {
      const updated = await apiService.addMeetingShares(meetingId, { user_ids: Array.from(selectedIds) });
      setShares(updated);
      setSelectedIds(new Set());
      toast.success(t('file.share-modal.add-success'));
    } catch (e) {
      reportError(e, t('file.share-modal.add-error'));
    } finally {
      setAddingSelected(false);
    }
  };

  const handleAddEmail = async () => {
    const email = emailInput.trim();
    if (!email || addingEmail) return;
    setAddingEmail(true);
    try {
      const updated = await apiService.addMeetingShares(meetingId, { emails: [email] });
      const found = updated.some(u => u.email.toLowerCase() === email.toLowerCase());
      setShares(updated);
      setEmailInput('');
      if (found) {
        toast.success(t('file.share-modal.add-success'));
      } else {
        toast.warning(t('file.share-modal.email-not-found', { email }));
      }
    } catch (e) {
      reportError(e, t('file.share-modal.add-error'));
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRevoke = async (userId: number) => {
    if (revokingId !== null) return;
    setRevokingId(userId);
    try {
      await apiService.removeMeetingShare(meetingId, userId);
      setShares(prev => prev.filter(u => u.id !== userId));
      toast.success(t('file.share-modal.revoke-success'));
    } catch (e) {
      reportError(e, t('file.share-modal.revoke-error'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Modal
      show={show}
      handleClose={onClose}
      style={{
        borderRadius: '1.25rem',
        width: '32rem',
        maxWidth: 'min(32rem, 95vw)',
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      header={
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden><Users /></span>
          <div>
            <h2 className={styles.title}>{t('file.share-modal.heading')}</h2>
            {meetingName && <p className={styles.subtitle}>{meetingName}</p>}
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading && <p className={styles.hint}>{t('file.share-modal.loading')}</p>}

        {!loading && forbidden && (
          <p className={styles.forbidden}>{t('file.share-modal.forbidden')}</p>
        )}

        {!loading && !forbidden && (
          <>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('file.share-modal.access-list-heading')}</h3>
              {shares.length === 0 ? (
                <p className={styles.hint}>{t('file.share-modal.access-empty')}</p>
              ) : (
                <ul className={styles.userList}>
                  {shares.map(user => (
                    <li key={user.id} className={styles.userRow}>
                      <span className={styles.avatar} aria-hidden>{initials(user)}</span>
                      <span className={styles.userInfo}>
                        <span className={styles.userEmail}>{user.email}</span>
                        {user.full_name && <span className={styles.userName}>{user.full_name}</span>}
                      </span>
                      <button
                        type="button"
                        className={styles.revokeBtn}
                        disabled={revokingId === user.id}
                        onClick={() => handleRevoke(user.id)}
                        aria-label={t('file.share-modal.revoke')}
                        title={t('file.share-modal.revoke')}
                      >
                        <TrashIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('file.share-modal.add-heading')}</h3>

              {usersLoading && <p className={styles.hint}>{t('file.share-modal.loading')}</p>}

              {!usersLoading && candidateUsers && (
                <>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={t('file.share-modal.search-placeholder') || ''}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {filteredCandidates.length === 0 ? (
                    <p className={styles.hint}>{t('file.share-modal.users-empty')}</p>
                  ) : (
                    <ul className={styles.userList}>
                      {filteredCandidates.map(user => (
                        <li key={user.id} className={styles.userRow}>
                          <label className={styles.userCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(user.id)}
                              onChange={() => toggleSelected(user.id)}
                            />
                            <span className={styles.avatar} aria-hidden>{initials(user)}</span>
                            <span className={styles.userInfo}>
                              <span className={styles.userEmail}>{user.email}</span>
                              {user.full_name && <span className={styles.userName}>{user.full_name}</span>}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={!selectedIds.size || addingSelected}
                    onClick={() => void handleAddSelected()}
                  >
                    {addingSelected
                      ? t('file.share-modal.loading')
                      : t('file.share-modal.add-selected', { count: selectedIds.size })}
                  </button>
                </>
              )}

              {!usersLoading && !candidateUsers && (
                <div className={styles.emailRow}>
                  <input
                    type="email"
                    className={styles.searchInput}
                    placeholder={t('file.share-modal.email-placeholder') || ''}
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleAddEmail();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    disabled={!emailInput.trim() || addingEmail}
                    onClick={() => void handleAddEmail()}
                  >
                    {addingEmail ? t('file.share-modal.loading') : t('file.share-modal.add-email')}
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ShareMeetingModal;
